/**
 * Every call to the backend.
 *
 * Two rules worth keeping:
 *   1. Nothing else in the app calls fetch. Errors, offline detection and the
 *      sign-in cookie are handled once, here.
 *   2. A child never sees an error code. `ApiError` carries the detail for the
 *      parent; the screens show a plain sentence.
 */

export class ApiError extends Error {
  constructor(
    public status: number,
    public detail: string,
    public kind: 'offline' | 'auth' | 'server' | 'request' | 'locked' | 'asleep' = 'server',
    // A message with a word or name inside also comes as the sentence with a
    // gap and the word, so a grown-up screen can translate it (round 14).
    public template?: string,
    public values?: Record<string, string>,
  ) {
    super(detail)
  }
}

/**
 * PIN tickets (D-065, 8 Oct 2026).
 *
 * Typing a PIN earns a ticket from the server, and the server checks the
 * ticket, not this screen. They are kept here, in memory only, so closing
 * the tab forgets them. Only the child who is practising right now has their
 * ticket sent; going back to Home stops sending it, which is what keeps a
 * sibling who picks up the tablet out.
 */
const tickets = {
  child: new Map<string, { token: string; at: number }>(),
  active: null as string | null,
  grownUp: null as { token: string; at: number } | null,
}

// How long a child can be away (at Home) and come back without the PIN.
//
// Two minutes, long enough to go from the summary to the garden. It was
// fifteen until 22 Oct 2026, and the PIN audit found the hole in that: Home
// is shared, so for fifteen minutes after Charles went back to it, Mark
// could tap Charles's card and be in as Charles, buddy, words and all.
const CHILD_FRESH_MS = 2 * 60 * 1000

export const unlock = {
  /** This child's ticket, from a PIN or from making their PIN. */
  setChild(learnerId: string, token: string): void {
    // A new child typing their PIN means the tablet changed hands, so the
    // one before them no longer gets back in without theirs.
    for (const [id, t] of tickets.child) if (id !== learnerId) t.at = 0
    tickets.child.set(learnerId, { token, at: Date.now() })
    tickets.active = learnerId
  },
  /** True if this child unlocked recently enough to go straight back in. */
  isFresh(learnerId: string): boolean {
    const t = tickets.child.get(learnerId)
    return Boolean(t && Date.now() - t.at < CHILD_FRESH_MS)
  },
  /** Start sending this child's ticket (they are the one using the app now). */
  use(learnerId: string | null): void {
    tickets.active = learnerId
    const t = learnerId ? tickets.child.get(learnerId) : null
    if (t) t.at = Date.now()
  },
  setGrownUp(token: string): void {
    tickets.grownUp = { token, at: Date.now() }
  },
  hasGrownUp(): boolean {
    return Boolean(tickets.grownUp && Date.now() - tickets.grownUp.at < 29 * 60 * 1000)
  },
  /** Leaving Settings: the grown-up's ticket must not stay behind for a child. */
  dropGrownUp(): void {
    tickets.grownUp = null
  },
  header(): string {
    return this.headerFor(tickets.active)
  },
  /**
   * The ticket for one particular child. The answer outbox sends a child's
   * answers with that child's ticket, even after a sibling has taken over
   * the tablet (offline-first sessions, 9 Oct 2026).
   */
  headerFor(learnerId: string | null): string {
    const out: string[] = []
    const child = learnerId ? tickets.child.get(learnerId) : null
    if (child) out.push(child.token)
    if (tickets.grownUp) out.push(tickets.grownUp.token)
    return out.join(',')
  },
}

/**
 * `quiet`: for work in the background (the answer outbox). A "PIN needed"
 * reply then stays between the caller and this function, instead of sending
 * whoever is using the app back to Home. `forLearner`: whose ticket to send.
 */
interface CallOptions { quiet?: boolean; forLearner?: string }

async function call<T>(path: string, init?: RequestInit, opts: CallOptions = {}): Promise<T> {
  let res: Response
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    const held = opts.forLearner ? unlock.headerFor(opts.forLearner) : unlock.header()
    if (held) headers['X-Unlock'] = held
    res = await fetch(path, {
      credentials: 'include',
      headers,
      ...init,
    })
  } catch {
    // fetch only rejects when the network itself failed.
    throw new ApiError(0, 'No internet. Word Sprout needs it.', 'offline')
  }

  if (res.status === 204) return undefined as T

  const text = await res.text()
  let body: any = null
  try {
    body = text ? JSON.parse(text) : null
  } catch {
    body = { detail: text }
  }

  if (!res.ok) {
    const detail = body?.detail ?? 'Something went wrong.'
    // A PIN is needed (or a ticket ran out). Not an error a child should
    // read: the app hears this event and goes back to Home, where tapping
    // their card asks for the PIN again.
    if (res.status === 403 && String(detail).startsWith('locked:')) {
      if (!opts.quiet) window.dispatchEvent(new CustomEvent('ws-locked', { detail: String(detail).slice(7) }))
      throw new ApiError(res.status, 'This needs a PIN first.', 'locked')
    }
    // A server error outside practice hours is almost always the database
    // asleep on purpose. Ask the health check, which answers without the
    // database, so the child reads "asleep, back at 7" rather than "broken".
    if (res.status >= 500 && path !== '/api/health') {
      try {
        const health = await (await fetch('/api/health', { credentials: 'include' })).json()
        if (health?.database === 'unreachable') {
          throw new ApiError(res.status, 'Word Sprout is asleep.', 'asleep')
        }
      } catch (err) {
        if (err instanceof ApiError) throw err
      }
    }
    const kind =
      res.status === 401 || res.status === 403 ? 'auth'
      : res.status >= 500 ? 'server'
      : 'request'
    throw new ApiError(res.status, String(detail), kind, body?.template, body?.values)
  }
  return body as T
}

const post = <T,>(path: string, body?: unknown) =>
  call<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) })

// ------------------------------------------------------------------- types

/**
 * A word somebody asked to learn.
 *
 *   added    — it is in the child's pool
 *   waiting  — new to the app, so a grown-up decides
 *   blocked  — a safety check said no; no grown-up was troubled
 *   declined — a grown-up said no
 *
 * `blocked` arrives as a normal 200 rather than an error. It happened and it
 * is written down; only the child's reading of it is a refusal.
 */
export interface WordRequest {
  request_id: string
  asked: string
  status: 'added' | 'waiting' | 'blocked' | 'declined'
  definition: string | null
  note: string | null
  created_at?: string
  /** Parent view only. */
  child_name?: string
  headword?: string
  asked_by?: 'child' | 'parent'
  example?: string | null
  part_of_speech?: string | null
  difficulty_band?: number | null
  source?: string | null
}


/** One meaning of a word, for a grown-up choosing which one a page means. */
export interface PageSense {
  word_sense_id: string
  definition: string
  part_of_speech: string | null
}

/** One word a pasted page turned out to have, and what a grown-up needs to know. */
export interface PageWord {
  word: string
  sentence: string
  definition: string | null
  /** Every meaning the app knows. More than one: the grown-up picks. */
  senses: PageSense[]
  in_catalogue: boolean
  /** This child has it already. */
  already_has: boolean
  /** A check said no. Shown rather than hidden. */
  blocked: boolean
}

export interface PageWords {
  title: string
  book_id: string
  words: PageWord[]
  note: string
}

/** A ticked word and, for a word with several, the meaning chosen. */
export interface PageChoice {
  word: string
  word_sense_id?: string
}

/** A book on a child's shelf, looked up by the 13 digits under its barcode. */
export interface Book {
  book_id: string
  isbn: string | null
  title: string
  subtitle: string | null
  authors: string | null
  publisher: string | null
  published: string | null
  page_count: number | null
  cover_url: string | null
  subjects: string | null
  blurb: string | null
  source: string | null
  status: 'reading' | 'finished' | 'want'
  added_at: string
  finished_at: string | null
  /** How many words in the pool came from this book. */
  words_from_it?: number
  /** Book, film or show (11 Oct 2026). */
  kind?: 'book' | 'film' | 'show'
  /** The grown-up's gate: nothing is picked from a source until it is approved. */
  approval?: 'waiting' | 'approved' | 'declined'
  added_via?: string | null
  /** The counter: how many days words were picked from it, and the last. */
  times_used?: number
  last_used_on?: string | null
}

/** A child's shelf, and which round of using the books they are on. */
export interface Shelf {
  books: Book[]
  /** Every approved book is used once before any is used twice. */
  round: number | null
  used_this_round: number
  approved: number
  next_book_id: string | null
}

/** What a catalogue knows about one ISBN. Nothing is on a shelf yet. */
export interface BookLookup {
  isbn: string
  isbn_pretty: string
  title: string
  subtitle?: string | null
  authors?: string | null
  publisher?: string | null
  published?: string | null
  page_count?: number | null
  cover_url?: string | null
  subjects?: string | null
  description?: string | null
  source: string
}

export type NextScreen = 'signin' | 'consent' | 'subscribe' | 'child_setup' | 'home'

export interface Resumable {
  learner_id: string
  display_name: string
  session_id: string
  cursor: number
  planned_count: number
}

export interface Child {
  learner_id: string
  display_name: string
  school_year: number
  year_offset: number
  birth_month: string | null
  birth_date?: string | null
  first_language: string
  avatar_kind: string
  avatar_ref: string | null
  status: string
  effective_year?: number
  pool_size?: number
  streak_days?: number
  /** The child's chosen colour theme. Arrives with the card so the
   *  app never paints in the wrong colours and then repaints. */
  theme?: string
  /** Whether a PIN is set. Never the PIN, never its hash. */
  has_pin?: boolean
  resume?: { session_id: string; cursor: number; planned_count: number } | null
}

/** A child's tablet signed in with a device code, as Settings lists it. */
export interface Device {
  device_id: string
  label: string
  added_at: string
  last_used_at: string | null
}

export interface GateState {
  signed_in: boolean
  consent_needed: boolean
  consent_version: number
  has_children: boolean
  next_screen: NextScreen
  /** `device_label` is set on a child's device signed in with a device code. */
  parent: {
    parent_id: string; email: string; display_name: string; device_label?: string | null
    /** Tells families apart for the answer outbox; not the parent's id. */
    family_tag?: string
    /** Spoken at home besides English, and the grown-up screens' language (null: English). */
    home_language?: string | null; grown_up_language?: string | null
  } | null
  children: Child[] | null
  resumable: Resumable[] | null
  /**
   * Which sign-in the server is doing. 'local' fakes it with no password and
   * is only ever right on a laptop; 'google' verifies a real token.
   *
   * It comes from the server rather than a build flag because one bundle
   * runs in both places, and only the server knows which it is.
   */
  auth_mode?: 'local' | 'google'
  /**
   * Whether a stranger can create an account. False until payment is switched
   * on (WS_SIGNUP_OPEN on the server). The front page reads it to decide what
   * "Try it free" does.
   */
  signup_open?: boolean
  /** 'email_plus' when this family gives consent by email (services/consent.py). */
  consent_method?: 'tick' | 'email_plus'
  /** The consent email waiting for the parent's click, if any. */
  consent_pending?: { email: string; asked_at: string; asks_sent: number; sent?: boolean } | null
  /** The trial kept only on this device is switched on (WS_TRY_ON_DEVICE). */
  try_on_device?: boolean
  /** Firebase web config, present only in 'google' mode. Public by design. */
  firebase?: {
    apiKey: string
    authDomain: string
    projectId: string
    appId: string
  } | null
}

export interface ConsentText {
  version: number
  title: string
  points: string[]
  tick_label: string
}

export interface SessionInfo {
  session_id: string
  learner_id: string
  status: string
  cursor: number
  planned_count: number
  resumed: boolean
  interests_due?: boolean
  plan: { module: string; item_type: string; state: string }[]
  map: { module: string; count: number; start: number; end: number }[]
}

export interface Question {
  session_id: string
  position: number
  total: number
  module: string
  item_type: string
  mastery_vector: string | null
  item_version_id: string | null
  payload: Record<string, any>
}

/** A whole session, for doing it on the device (GET /api/sessions/{id}/bundle). */
export interface Bundle {
  session_id: string
  planned_count: number
  cursor: number
  questions: (Question & { key: { answer: unknown; answer_index: number | null; about?: WordAbout | null } })[]
}

/** The word behind a question, shown after a wrong or skipped answer. */
export interface WordAbout {
  word: string
  definition: string | null
  example: string | null
}

export interface AnswerResult {
  trial_id: string
  is_correct: boolean | null
  is_first_attempt: boolean
  edit_distance: number | null
  correct_answer: string | string[] | null
  next_position: number
  finished: boolean
  /** Only on the device: the bundle's word, meaning and example. */
  about?: WordAbout | null
}

/** The starting-point check (beta). services/placement.py. */
export interface StartingCheckItem {
  index: number
  word: string
  options: string[]
  practice: boolean
  /** Only on the two practice items. */
  answer_index?: number
}
export interface StartingCheck {
  check_id: string; version: number; items: StartingCheckItem[]
  /** Opens only this child: the grown-up's ticket is dropped for the child's turn. */
  ticket?: string
}
export interface StartingCheckAnswer { index: number; choice: number | null; ms: number; replays: number }
export interface StartingCheckResult {
  check_id: string
  version: number
  finished: boolean
  finished_at: string | null
  school_year: number
  per_group: number
  lower_right: number | null
  middle_right: number | null
  upper_right: number | null
  /** -1, 0 or +1 from the school year. */
  suggested_offset: number | null
  confidence: 'normal' | 'low' | null
  chosen_offset: number | null
  current_offset: number | null
}

export interface Summary {
  session_id: string
  learner_id: string
  status: string
  /** This was the five more after a practice. */
  extra?: boolean
  /** Offer "Practise 5 more": true only at the end of an ordinary practice. */
  can_practise_more?: boolean
  answered: number
  planned: number
  assessed: number
  correct: number
  /** Distinct words touched in this session. */
  words_today: number
  /** How many of those the child had never met before today. */
  words_new_today: number
  /** Every word ever fully learned. Counted from a stamp that is never
   *  cleared, so it cannot be smaller than it was yesterday (D-048). */
  words_grown_total: number
  /** For the parent's dashboard only. A percentage that can go backwards is
   *  never shown to the child — see D-048. */
  accuracy: number | null
  streak_days: number
  /** So Mistake Lab can speak a missed word in the child's own language as
   *  well as in English. */
  first_language: string
  new_badges?: { code: string; name: string; emoji: string; description: string }[]
  mistakes: {
    word: string | null
    your_answer: string | null
    definition: string | null
    example: string | null
    translation: string | null
    trial_type: string
  }[]
}

export interface Interest {
  interest_id: string
  label: string
  emoji: string | null
  sort_order: number
}

export interface BadgeBoard {
  display_name: string
  found: { code: string; name: string; emoji: string; description: string; awarded_at: string }[]
  working_towards: {
    code: string; name: string; emoji: string; description: string
    now: number; target: number; unit: string
  }[]
  hidden_count: number
  counters: {
    words_met: number; words_mastered: number; trials: number
    minutes: number; sessions: number; streak_days: number
  }
  sibling_news: { display_name: string; name: string; emoji: string; awarded_at: string }[]
}

export interface Garden {
  display_name: string
  words_met: number
  words_mastered: number
  newest_sprout: {
    word: string; definition: string | null; example_sentence: string | null
    updated_at: string; streak: number; interval_days: number
  } | null
  shelves: {
    word_list_id: string; name: string; source_kind: string
    total: number; grown: number | null
  }[]
  words: {
    word: string; definition: string | null; example_sentence: string | null
    status: string; last_seen_at: string | null
    streak: number; interval_days: number; grown: number
  }[]
  empty_message: string | null
}

export interface ReportSettings {
  frequency: 'off' | 'daily' | 'weekly' | 'monthly'
  choices: string[]
  email: string | null
  /** False until an email service is connected; nothing is sent until then. */
  email_ready: boolean
}

export interface BillingStatus {
  signup_open: boolean
  /** Stripe is configured. False means payment cannot start yet. */
  ready: boolean
  /** On the allow list: never asked to pay. */
  comped: boolean
  status: string
  trial_end: string | null
  current_period_end: string | null
  trial_days: number
  needs_card: boolean
  can_manage: boolean
  price_label: string
}

/** A sprout that grows into a giant sequoia. Counted from grown words only. */
export interface TreeState {
  display_name: string
  grown: number
  /** Every word in three plots: ripe (grown), growing, new. See WordField. */
  fields: { ripe: string[]; growing: string[]; new: string[] }
  stage: number
  name: string
  fact: string
  next_name: string | null
  next_at: number | null
  stage_at: number
  stages: { at: number; name: string }[]
  recent: string[]
}

export interface CrosswordEntry {
  number: number
  direction: 'across' | 'down'
  row: number
  col: number
  answer: string
  length: number
  clue: string
  word: string
}

export type Crossword =
  | { ready: false; message: string }
  | { ready: true; rows: number; cols: number; solution: string[]; entries: CrosswordEntry[] }

export type Mystery =
  | { ready: false; message: string }
  | {
      ready: true; answer: string; length: number; tries: number; word: string
      definition: string; sentence: string | null; full_sentence: string | null
    }

export type HangmanGame =
  | { ready: false; message: string }
  | {
      ready: true; answer: string; length: number; misses: number; word: string
      definition: string; sentence: string | null; full_sentence: string | null
    }

/** Every word a child has met, as stars. Kept tiny per word on purpose. */
export interface Universe {
  /** w: the word · g: 1 if fully grown · b: its grade · d: days since last met */
  stars: { w: string; g: number; b: number | null; d: number | null }[]
  galaxies: { band: number; name: string }[]
  total: number
  grown: number
}

export interface WordMapNode {
  id: string
  word: string
  definition: string | null
  /** Has been fully learned at some point. Never goes back to false — the
   *  map only grows (D-048). */
  grown: boolean
  successes: number
  band: number | null
  /** Days since the child last met this word, from the trials. Null if they
   *  somehow have it in the pool without ever having seen it. */
  days_since: number | null
  last_met: string | null
  links: number
  focus: boolean
}

export interface WordMap {
  nodes: WordMapNode[]
  edges: { a: string; b: string; kind: 'story' | 'meaning' }[]
  focus: string | null
  total_words: number
  total_grown: number
  /** The time window currently applied, in days. Null means everything. */
  days: number | null
  /** How many words sit in each stretch, so the filter can say what it will
   *  show before it is tapped. */
  bands: { days: number | null; label: string; count: number }[]
  empty_message: string | null
}

export interface Levels {
  levels: {
    band: number
    name: string
    /** Words at this difficulty the child has met. */
    met: number
    /** Of those, how many are fully grown. Counted from a stamp that is never
     *  cleared, so it cannot go down (D-048). */
    grown: number
    /** How many grown words clear the level. A fixed number rather than a
     *  percentage, so the bar fills and never empties. */
    target: number
    cleared: boolean
    cleared_at: string | null
  }[]
  /** The lowest level with words in it that is not yet cleared. */
  working_on: number | null
  cleared_count: number
  total_grown: number
  newly_cleared: number[]
  empty_message: string | null
}

/** The parent's at-a-glance view: this week or this month (services/overview.py). */
export interface Overview {
  display_name: string
  period: 'week' | 'month'
  start: string
  end: string
  tiles: {
    minutes: number; minutes_before: number
    days_practised: number; days_before: number
    words_grown: number; grown_before: number
    new_words: number; new_before: number
  }
  bars: { label: string; date: string; minutes: number }[]
  trend: { label: string; grown: number }[]
  tricky: { word: string; met: number; unaided: number; missed: number }[]
  tree: {
    name: string; grown: number; stage?: number; stage_at?: number
    next_name: string | null; next_at: number | null
  }
  badges: { name: string; emoji: string }[]
  grown_words: string[]
  new_words: string[]
  rechecked: number
}

export interface Progress {
  learner_id: string
  display_name: string
  effective_year: number
  totals: Record<string, any>
  pool_size: number
  streak_days: number
  recent_accuracy: number | null
  by_day: { event_date: string; trials: number; correct: number; assessed: number }[]
  by_module: { module: string; trials: number; correct: number; assessed: number }[]
  hardest_words: { word: string; seen: number; missed: number }[]
  empty_message: string | null
}

// ------------------------------------------------------------------- calls

export const api = {
  state: () => call<GateState>('/api/state'),
  /**
   * `token` is a Firebase ID token in 'google' mode and ignored in 'local'.
   * The server decides which; the browser only supplies what it has.
   */
  signIn: (token?: string) => post<GateState>('/api/signin', token ? { token } : {}),
  signOut: async () => {
    const out = await post<{ next_screen: NextScreen }>('/api/signout')
    forgetOnThisBrowser(false)
    return out
  },

  // Device codes: a child's tablet signed in without Google (22 Oct 2026).
  makeDeviceCode: () => post<{ code: string; expires_in: number }>('/api/devices/code'),
  claimDevice: (code: string, label: string) =>
    post<GateState>('/api/devices/claim', { code, label }),
  devices: () => call<{ devices: Device[] }>('/api/devices'),
  removeDevice: (deviceId: string) => post<{ removed: boolean }>(`/api/devices/${deviceId}/remove`),

  consentText: () => call<ConsentText>('/api/consent/text'),
  acceptConsent: (version: number) =>
    post<GateState>('/api/consent', { accepted: true, version }),
  /** The links in the email-plus emails. No sign-in: the token is the proof. */
  consentLink: (token: string, kind: 'consent' | 'withdraw') =>
    post<{ valid: boolean; email?: string; children?: number; given_at?: string | null }>(
      '/api/consent/link', { token, kind }),
  confirmConsent: (token: string) => post<{ confirmed: boolean }>('/api/consent/confirm', { token }),
  withdrawConsent: (token: string) =>
    post<{ withdrawn: boolean; children_deleted: number }>('/api/consent/withdraw', { token, kind: 'withdraw' }),
  /** After consent: the device trial's words join a child's list. */
  fromDeviceTrial: (learnerId: string, words: { word_sense_id: string; missed: boolean }[]) =>
    post<{ added: number; skipped: number }>(`/api/children/${learnerId}/from-device-trial`, { words }),

  children: () => call<{ children: Child[] }>('/api/children'),
  /** Save the child's colour choice. Fire and forget: the app has
   *  already changed colour, and a failed save is not worth a dialog. */
  setTheme: (learnerId: string, theme: string) =>
    call<{ saved: boolean }>('/api/settings', {
      method: 'PUT',
      body: JSON.stringify({ scope: 'learner', scope_id: learnerId,
                             key: 'theme', value: theme }),
    }),
  /** One of a child's own settings (e.g. 'trophy_layout'), or null. */
  learnerSetting: async (learnerId: string, key: string): Promise<string | null> => {
    const r = await call<{ settings: Record<string, string | null> }>(
      `/api/settings?scope=learner&scope_id=${encodeURIComponent(learnerId)}`)
    return r.settings[key] ?? null
  },
  setLearnerSetting: (learnerId: string, key: string, value: string) =>
    call<{ saved: boolean }>('/api/settings', {
      method: 'PUT',
      body: JSON.stringify({ scope: 'learner', scope_id: learnerId, key, value }),
    }),
  /** `pin` is in this response once, and readable nowhere else afterwards. */
  addChild: (body: Partial<Child>) =>
    post<{ child: Child; pin?: string; ticket?: string; effective_year: number }>('/api/children', body),
  unlockChild: (learnerId: string, pin: string) =>
    post<{ unlocked: boolean; ticket: string }>(`/api/children/${learnerId}/unlock`, { pin }),
  /** A grown-up sets a child's PIN; `pin` is the one the child chose. */
  setChildPin: (learnerId: string, pin?: string) =>
    post<{ pin: string; emailed: boolean; ticket: string }>(
      `/api/children/${learnerId}/pin`, pin ? { pin } : {}),
  grownUp: () => call<{ has_pin: boolean; pin_length: number | null; unlocked: boolean; minutes: number }>('/api/grown-up'),
  makeGrownUpPin: (pin: string, answer?: string) =>
    post<{ ticket: string }>('/api/grown-up/pin', { pin, answer }),
  unlockGrownUp: (pin: string) => post<{ ticket: string }>('/api/grown-up/unlock', { pin }),
  forgotGrownUp: (body: { token?: string; answer?: string }) =>
    post<{ cleared: boolean }>('/api/grown-up/forgot', body),
  updateChild: (id: string, body: Partial<Child>) =>
    call<{ child: Child }>(`/api/children/${id}`, {
      method: 'PATCH', body: JSON.stringify(body),
    }),
  archiveChild: (id: string) => post<{ child: Child }>(`/api/children/${id}/archive`),
  // The starting-point check (beta), Sam's build list item 10 (29 Oct 2026).
  startingCheck: (id: string) => post<StartingCheck>(`/api/children/${id}/starting-check`),
  lastStartingCheck: (id: string) =>
    call<{ check: StartingCheckResult | null }>(`/api/children/${id}/starting-check`),
  finishStartingCheck: (checkId: string, answers: StartingCheckAnswer[]) =>
    post<{ check_id: string; finished: boolean }>(`/api/starting-check/${checkId}/finish`, { answers }),
  /** Behind the grown-up PIN: the counts and the suggestion. */
  startingCheckResult: (checkId: string) =>
    call<StartingCheckResult>(`/api/starting-check/${checkId}`),
  chooseStart: (checkId: string, offset: number) =>
    post<StartingCheckResult>(`/api/starting-check/${checkId}/choose`, { offset }),

  drawnAvatars: () => call<{ emoji: string[] }>('/api/avatars/drawn'),
  setBuddy: (id: string, code: string) =>
    call<{ child: Child }>(`/api/children/${id}/avatar/buddy`, { method: 'PUT', body: JSON.stringify({ code }) }),
  setDrawnAvatar: (id: string, emoji: string) =>
    call<{ child: Child }>(`/api/children/${id}/avatar/drawn`, {
      method: 'PUT', body: JSON.stringify({ emoji }),
    }),
  uploadAvatar: async (id: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    // No Content-Type header: the browser sets the multipart boundary itself.
    // The PIN ticket, like every other call: without it a family with two
    // children was always refused (audit, 12 Oct 2026).
    const held = unlock.header()
    const res = await fetch(`/api/children/${id}/avatar`, {
      method: 'POST', credentials: 'include', body: form,
      headers: held ? { 'X-Unlock': held } : undefined,
    })
    const body = await res.json().catch(() => null)
    if (!res.ok) {
      throw new ApiError(res.status, String(body?.detail ?? 'Upload failed.'),
        res.status >= 500 ? 'server' : 'request')
    }
    return body as { child: Child }
  },
  removeAvatar: (id: string) =>
    call<{ child: Child }>(`/api/children/${id}/avatar`, { method: 'DELETE' }),

  startSession: (learnerId: string, length?: number) =>
    post<SessionInfo>(`/api/children/${learnerId}/session`, { length, resume: true }),
  /** "Practise 5 more", offered once at the end of a practice. */
  practiseMore: (learnerId: string) =>
    post<SessionInfo>(`/api/children/${learnerId}/session`, { extra: true, resume: true }),
  session: (id: string) => call<SessionInfo>(`/api/sessions/${id}`),
  question: (id: string, position: number) =>
    call<Question>(`/api/sessions/${id}/question/${position}`),
  answer: (id: string, body: Record<string, unknown>) =>
    post<AnswerResult>(`/api/sessions/${id}/answer`, body),
  bundle: (id: string) => call<Bundle>(`/api/sessions/${id}/bundle`),
  /** For the outbox: one child's answer, sent in the background. */
  sendAnswer: (id: string, learnerId: string, body: Record<string, unknown>) =>
    call<AnswerResult>(`/api/sessions/${id}/answer`, { method: 'POST', body: JSON.stringify(body) },
      { quiet: true, forLearner: learnerId }),
  finish: (id: string) => post<Summary>(`/api/sessions/${id}/finish`),

  interests: (learnerId: string) =>
    call<{ all: Interest[]; chosen: string[]; due: boolean; asked_at: string | null }>(
      `/api/children/${learnerId}/interests`),
  setInterests: (
    learnerId: string,
    body: { interest_ids: string[]; new_labels: string[]; skipped: boolean },
  ) =>
    call<{ chosen: string[]; all: Interest[] }>(`/api/children/${learnerId}/interests`, {
      method: 'PUT', body: JSON.stringify(body),
    }),
  badges: (learnerId: string) => call<BadgeBoard>(`/api/children/${learnerId}/badges`),
  universe: (learnerId: string) => call<Universe>(`/api/children/${learnerId}/universe`),
  overview: (learnerId: string, period: 'week' | 'month') =>
    call<Overview>(`/api/children/${learnerId}/overview?period=${period}`),
  tree: (learnerId: string) => call<TreeState>(`/api/children/${learnerId}/tree`),
  crossword: (learnerId: string) => call<Crossword>(`/api/children/${learnerId}/games/crossword`),
  mystery: (learnerId: string) => call<Mystery>(`/api/children/${learnerId}/games/mystery`),
  hangman: (learnerId: string) => call<HangmanGame>(`/api/children/${learnerId}/games/hangman`),
  billing: () => call<BillingStatus>('/api/billing'),
  billingCheckout: () => post<{ url: string }>('/api/billing/checkout'),
  billingPortal: () => post<{ url: string }>('/api/billing/portal'),
  setLanguage: (home_language: string | null, grown_up_language: string) =>
    call<{ home_language: string | null; grown_up_language: string }>('/api/parent/language', {
      method: 'PUT', body: JSON.stringify({ home_language, grown_up_language }),
    }),
  reportSettings: () => call<ReportSettings>('/api/reports/settings'),
  setReportFrequency: (frequency: string) =>
    call<{ frequency: string }>('/api/reports/settings', {
      method: 'PUT', body: JSON.stringify({ frequency }),
    }),
  reportPreview: (frequency: string) =>
    call<{ subject: string; text: string; html: string; start: string; end: string }>(
      `/api/reports/preview?frequency=${encodeURIComponent(frequency)}`),
  levels: (learnerId: string) =>
    call<Levels>(`/api/children/${learnerId}/levels`),

  wordMap: (
    learnerId: string, focus = '', days: number | null = null,
    limit: number | null = null,
  ) => {
    const q = new URLSearchParams()
    if (focus) q.set('focus', focus)
    if (days) q.set('days', String(days))
    // A phone asks for fewer words. Forty blobs on a 360px screen is a grey
    // smudge, not a map.
    if (limit) q.set('limit', String(limit))
    const tail = q.toString()
    return call<WordMap>(
      `/api/children/${learnerId}/wordmap` + (tail ? `?${tail}` : ''))
  },

  // ------------------------------------------------- words somebody asked for

  askForWord: (learnerId: string, word: string, askedBy: 'child' | 'parent') =>
    post<WordRequest>(
      `/api/children/${learnerId}/word-requests`,
      { word, asked_by: askedBy }),

  myWordRequests: (learnerId: string) =>
    call<{ requests: WordRequest[] }>(
      `/api/children/${learnerId}/word-requests`),

  wordRequestsForParent: () =>
    call<{ requests: WordRequest[] }>('/api/word-requests'),

  approveWordRequest: (requestId: string) =>
    post<WordRequest>(`/api/word-requests/${requestId}/approve`),

  declineWordRequest: (requestId: string, note?: string) =>
    post<WordRequest>(`/api/word-requests/${requestId}/decline`, { note }),

  // ------------------------------------------------------- the reading list

  lookUpIsbn: (isbn: string) => call<BookLookup>(`/api/isbn/${isbn}`),

  readingList: (learnerId: string) =>
    call<Shelf>(`/api/children/${learnerId}/books`),

  addBook: (
    learnerId: string,
    body: { isbn?: string; title?: string; authors?: string; status?: string; kind?: string },
  ) => post<Book>(`/api/children/${learnerId}/books`, body),

  setBookStatus: (bookId: string, status: string) =>
    post<Book>(`/api/books/${bookId}/status`, { status }),

  setBookApproval: (bookId: string, approval: 'waiting' | 'approved' | 'declined') =>
    post<Book>(`/api/books/${bookId}/approval`, { approval }),

  removeBook: (bookId: string) =>
    post<{ removed: boolean }>(`/api/books/${bookId}/remove`),

  // Text copied off a page with the phone's own reader (27 Oct 2026). The
  // photo never comes here; the text is used for the answer and not kept.
  findBookPageWords: (learnerId: string, bookId: string, text: string) =>
    post<PageWords>(
      `/api/children/${learnerId}/book/page-words`, { book_id: bookId, text }),

  addFromBook: (
    learnerId: string, title: string, choices: PageChoice[], bookId = '',
  ) => post<{ added: number; words: WordRequest[] }>(
      `/api/children/${learnerId}/book`, { title, choices, book_id: bookId }),

  garden: (learnerId: string, search = '', sort = 'newest') =>
    call<Garden>(
      `/api/children/${learnerId}/garden?search=${encodeURIComponent(search)}&sort=${sort}`),

  leave: (id: string) => post<{ next_screen: NextScreen }>(`/api/sessions/${id}/leave`),
  summary: (id: string) => call<Summary>(`/api/sessions/${id}/summary`),
  flag: (id: string, itemVersionId: string) =>
    post(`/api/sessions/${id}/flag`, { item_version_id: itemVersionId }),

  progress: (learnerId: string) =>
    call<Progress>(`/api/children/${learnerId}/progress`),
  parentGate: (answer: string) => post<{ passed: boolean }>('/api/parent-gate', { answer }),
  deleteAccount: async (confirm: string) => {
    const out = await post('/api/account/delete', { confirm })
    forgetOnThisBrowser(true)
    return out
  },
}

/**
 * What this browser keeps of a family's practice, cleared when they leave
 * (Codex round 22, finding 6): the kept sessions (every question with its
 * answer), answers the server refused, and the PIN tickets. On sign-out,
 * answers still waiting to be sent stay: they belong to this family and go
 * when it signs in again (Codex round 20, finding 4). A deleted account has
 * nowhere to send them, so they go too.
 */
export function forgetOnThisBrowser(deleted: boolean): void {
  tickets.child.clear()
  tickets.active = null
  tickets.grownUp = null
  try {
    const keys: string[] = []
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i)
      if (k && (k.startsWith('ws-session-v1:') || k === 'ws-outbox-held-v1'
        || (deleted && (k === 'ws-outbox-v1' || k === 'ws-outbox-family')))) keys.push(k)
    }
    keys.forEach((k) => window.localStorage.removeItem(k))
  } catch {
    // Storage blocked: nothing was kept in it.
  }
}

/**
 * A page of words for the trial kept on this device (services/try_on_device.py).
 *
 * Sent with no cookie and no PIN ticket: the server must not be able to tie
 * the request to a family. It gets a school year and a page number, nothing
 * else, and keeps nothing.
 */
export type TryQuestion = Bundle['questions'][number] & { word_sense_id: string }
export interface TryPage { year: number; page: number; last_page: number; words: number; questions: TryQuestion[] }
export async function tryWords(year: number, page: number): Promise<TryPage> {
  let res: Response
  try {
    res = await fetch(`/api/try/words?year=${year}&page=${page}`, { credentials: 'omit' })
  } catch {
    throw new ApiError(0, 'No internet. Word Sprout needs it.', 'offline')
  }
  if (!res.ok) throw new ApiError(res.status, 'That did not work. Try again in a moment.', 'server')
  return res.json() as Promise<TryPage>
}
